import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  BugReport as BugReportIcon,
  Timeline as TimelineIcon,
  Insights as InsightsIcon,
  Attachment as AttachmentIcon,
} from '@mui/icons-material';
import useQualityInsights from '../../hooks/useQualityInsights';
import QualityInsightsPanel from './QualityInsightsPanel';

const MetricTile = ({ icon, title, value, caption, color = 'primary' }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      background: (theme) =>
        theme.palette.mode === 'dark'
          ? theme.palette.grey[900]
          : `linear-gradient(135deg, ${theme.palette[color].light}15, ${theme.palette[color].main}20)`,
      borderRadius: 3,
      border: (theme) => `1px solid ${theme.palette.divider}`,
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box
          sx={{
            p: 1.2,
            borderRadius: 2,
            bgcolor: (theme) => theme.palette[color].main,
            color: (theme) => theme.palette[color].contrastText,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="overline" sx={{ letterSpacing: 1, opacity: 0.7 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, my: 0.5 }}>
            {value ?? '—'}
          </Typography>
          {caption && (
            <Typography variant="body2" color="text.secondary">
              {caption}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const QualityDashboard = ({ projectId }) => {
  const { loading, error, heatmap, trend, mttr, metadata, batches } = useQualityInsights(projectId);

  const summary = useMemo(() => {
    const totalIssues = metadata?.total_issues
      ?? heatmap?.series?.reduce((sum, series) => sum + series.buckets.reduce((s, b) => s + Number(b.v || 0), 0), 0)
      ?? 0;

    const latestTrend = trend?.series?.length ? trend.series[trend.series.length - 1] : null;
    const openNow = latestTrend?.open ?? 0;
    const closedNow = latestTrend?.closed ?? 0;

    const attachmentCoverage = metadata?.pct_with_attachment ?? 0;
    const componentCoverage = metadata?.pct_with_component ?? 0;

    const trackedComponents = heatmap?.series?.length ?? 0;

    return {
      totalIssues,
      openNow,
      closedNow,
      attachmentCoverage,
      componentCoverage,
      trackedComponents,
    };
  }, [heatmap, trend, metadata]);

  return (
    <Box sx={{ p: 4, bgcolor: 'background.default', minHeight: 'calc(100vh - 64px)' }}>
      <Box
        sx={{
          borderRadius: 4,
          px: 4,
          py: 5,
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.main})`
              : `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`,
          color: 'primary.contrastText',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.15,
            background:
              'radial-gradient(circle at top left, rgba(255,255,255,0.6), transparent 55%), radial-gradient(circle at bottom right, rgba(255,255,255,0.4), transparent 45%)',
          }}
        />
        <Stack spacing={2} sx={{ position: 'relative' }}>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>
            Quality Analytics
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: 640, opacity: 0.9 }}>
            Track defect density, velocity, and metadata health in real time. This dashboard reflects the latest Jira
            imports pulled into the Product Knowledge Base—no duplicate data, no manual sync.
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip
              color="default"
              variant="filled"
              label={`Issues tracked: ${summary.totalIssues}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'inherit', fontWeight: 600 }}
            />
            <Chip
              color="default"
              variant="filled"
              label={`Components covered: ${summary.trackedComponents}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'inherit', fontWeight: 600 }}
            />
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <MetricTile
            icon={<BugReportIcon fontSize="small" />}
            title="Open bugs"
            value={summary.openNow}
            caption="Currently unresolved (latest snapshot)"
            color="error"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricTile
            icon={<TimelineIcon fontSize="small" />}
            title="Closed last window"
            value={summary.closedNow}
            caption="Closed in the most recent weekly bucket"
            color="success"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricTile
            icon={<InsightsIcon fontSize="small" />}
            title="Component coverage"
            value={`${summary.componentCoverage?.toFixed?.(1) ?? summary.componentCoverage}%`}
            caption="Bugs tagged with a component"
            color="primary"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricTile
            icon={<AttachmentIcon fontSize="small" />}
            title="Attachment coverage"
            value={`${summary.attachmentCoverage?.toFixed?.(1) ?? summary.attachmentCoverage}%`}
            caption="Bugs including screenshots or files"
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricTile
            icon={<BugReportIcon fontSize="small" />}
            title="Total tracked issues"
            value={summary.totalIssues}
            caption="Imported via Jira sync"
            color="info"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricTile
            icon={<TimelineIcon fontSize="small" />}
            title="Components monitored"
            value={summary.trackedComponents}
            caption="Distinct components in heatmap"
            color="warning"
          />
        </Grid>
      </Grid>

      <QualityInsightsPanel
        loading={loading}
        error={error}
        heatmap={heatmap}
        trend={trend}
        mttr={mttr}
        metadata={metadata}
        batches={batches}
      />
    </Box>
  );
};

export default QualityDashboard;

